import { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';

export interface MxmPostCalendar {
  id: number;
  orgId: number;
  postId: number;
  post?: {
    id: number;
    title: string;
    subtitle?: string;
    contentType?: string;
    status: 'draft' | 'planned' | 'published';
  };
  plannedDate: Date | string;
  publishedDate?: Date | string;
  dayNumber: number;
  status: 'draft' | 'planned' | 'published' | 'cancelled';
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface QueryCalendarDto {
  orgId?: number;
  userId?: string;
  startDate?: string;
  endDate?: string;
  month?: string; // Format: YYYY-MM
}

// Status filter for calendar - only show planned and draft entries
const CALENDAR_STATUS_FILTER = ['planned', 'draft'] as const;

/**
 * Convert month label (e.g., "January 2025") to YYYY-MM format
 */
const monthLabelToYYYYMM = (monthLabel: string): string => {
  const [monthName, yearStr] = monthLabel.split(' ');
  const year = parseInt(yearStr);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthIndex = monthNames.indexOf(monthName);
  if (monthIndex === -1) {
    // Fallback to current month
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
};

export const useMxmCalendar = (
  orgId?: number,
  monthLabel?: string,
  enabled: boolean = true,
  userId?: string
) => {
  const [calendar, setCalendar] = useState<MxmPostCalendar[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatingEntryId, setUpdatingEntryId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchCalendar = useCallback(
    async (query?: QueryCalendarDto) => {
      if ((!orgId && !userId) || !enabled) return;

      setLoading(true);
      setError(null);
      try {
        const params: QueryCalendarDto = {
          ...(orgId && { orgId }),
          ...(userId && { userId }),
          ...query,
        };

        // Convert month label to YYYY-MM format if provided
        if (monthLabel && !params.month) {
          params.month = monthLabelToYYYYMM(monthLabel);
        }

        // Build params with status as multiple query params (status=planned&status=draft)
        const queryParams = new URLSearchParams();
        if (params.orgId) queryParams.append('orgId', params.orgId.toString());
        if (params.userId) queryParams.append('userId', params.userId);
        if (params.month) queryParams.append('month', params.month);
        if (params.startDate) queryParams.append('startDate', params.startDate);
        if (params.endDate) queryParams.append('endDate', params.endDate);

        // Add status parameters multiple times for proper format
        CALENDAR_STATUS_FILTER.forEach(status => {
          queryParams.append('status', status);
        });

        const endpoint = params.userId ? '/mxm/internal/calendar' : '/mxm/post-calendar';
        const { data } = await api.get<MxmPostCalendar[]>(
          `${endpoint}?${queryParams.toString()}`
        );
        setCalendar(data || []);
      } catch (err: unknown) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [orgId, userId, monthLabel, enabled]
  );

  useEffect(() => {
    if (enabled && (orgId || userId) && monthLabel) {
      fetchCalendar();
    }
  }, [orgId, userId, monthLabel, enabled, fetchCalendar]);

  const updateCalendarEntry = useCallback(
    async (id: number, updateData: { plannedDate?: string; dayNumber?: number; status?: string }) => {
      setUpdatingEntryId(id);
      setError(null);
      try {
        const { data } = await api.patch<MxmPostCalendar>(
          `/mxm/post-calendar/${id}`,
          updateData
        );
        // Update the local state
        setCalendar((prev) =>
          prev.map((entry) => (entry.id === id ? data : entry))
        );
        // Refetch to ensure consistency
        await fetchCalendar();
        return data;
      } catch (err: unknown) {
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setUpdatingEntryId(null);
      }
    },
    [fetchCalendar]
  );

  return {
    calendar,
    loading,
    updatingEntryId,
    error,
    fetchCalendar,
    refetch: fetchCalendar,
    updateCalendarEntry,
  };
};

